/**
 * Calendar server state: cards with deadlines of a board or of a workspace's boards, for a
 * visible date range. The calendar is only a view of existing cards: moving, resizing and
 * creating reuse the card endpoints (`PUT /cards/{id}/deadline`, `POST /columns/{id}/cards`).
 */
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import { invalidateBoard, type RecurrenceInput } from './api';
import type { Card, Label } from './types';

/**
 * An UPCOMING occurrence of a recurring series that is not a card yet (virtual). It has no card
 * id: `key` identifies it, `original_date` is its schedule date (the slot), `deadline` its
 * effective period after individual changes. It is edited through the series' source card.
 */
export interface CalendarOccurrence {
  key: string;
  rule_id: string;
  source_card_id: string;
  board_id: string;
  column_id: string;
  original_date: string;
  title: string;
  deadline: NonNullable<Card['deadline']>;
  is_event: boolean;
  has_exception: boolean;
  label_ids: string[];
  appearance: Card['appearance'];
  timezone: string;
}

/** A board a task of the personal calendar comes from (only the personal scope returns them). */
export interface CalendarSourceBoard {
  id: string;
  name: string;
  color: string | null;
  workspace_id: string | null;
  workspace_name: string | null;
  workspace_type: string | null;
  /** `personal`: the viewer's personal workspace (dated tasks); `team`: assigned tasks. */
  kind: 'personal' | 'team';
}

export interface CalendarData {
  cards: Card[];
  /** Upcoming occurrences of recurring series, computed by the server for the range. */
  occurrences?: CalendarOccurrence[];
  /** Active cards without any deadline (only when asked for with `includeUndated`). */
  undated?: Card[];
  /** Labels of the boards in scope (for the label filter). */
  labels: Label[];
  /** Personal scope only: the boards of the returned tasks. */
  boards?: CalendarSourceBoard[];
  /** Workspace / global / board scope: the boards of the calendar where a card can be created. */
  creatable_board_ids?: string[];
  /** Personal scope only: more undated tasks exist than were returned. */
  undated_truncated?: boolean;
}

export interface CalendarRangeQuery {
  /** Inclusive local dates `YYYY-MM-DD`. */
  start: string;
  end: string;
  includeCompleted: boolean;
  includeOverdue: boolean;
  /** Archived tasks come with their dates, flagged by `archived_at` (the server's default). */
  includeArchived?: boolean;
  /** Also fetch the tasks without a date (the panel's "No date" section). */
  includeUndated?: boolean;
  search: string;
  labelIds: string[];
  /** Workspace scope only: the visible boards (all when empty). */
  boardIds: string[];
}

/** How often a visible calendar re-reads its range (virtual occurrences turn into cards). */
export const CALENDAR_REFRESH_MS = 60_000;

/**
 * `me`: the viewer's own tasks across every board (the personal planner) - the same calendar,
 * fed by `GET /me/calendar`, which applies the planner's eligibility rule on the server.
 */
export type CalendarScope =
  | { kind: 'board'; boardId: string }
  | { kind: 'workspace'; workspaceId: string }
  /** Every board the viewer can see (personal, all workspaces, boards shared with them). */
  | { kind: 'all' }
  | { kind: 'me' };

function calendarRequest(scope: CalendarScope, range: CalendarRangeQuery, signal: AbortSignal) {
  const path =
    scope.kind === 'board'
      ? `/v1/boards/${scope.boardId}/calendar`
      : scope.kind === 'workspace'
        ? `/v1/workspaces/${scope.workspaceId}/calendar`
        : scope.kind === 'all'
          ? '/v1/me/boards/calendar'
          : '/v1/me/calendar';
  return api.get<CalendarData>(path, {
    signal,
    query: {
      range_start: range.start,
      range_end: range.end,
      include_completed: range.includeCompleted,
      include_overdue: range.includeOverdue,
      ...(range.includeArchived === false && { include_archived: false }),
      ...(range.includeUndated && { include_undated: true }),
      q: range.search.trim() || undefined,
    },
    repeat: {
      label_id: range.labelIds,
      ...(scope.kind !== 'board' && { board_id: range.boardIds }),
    },
  });
}

export function useCalendar(scope: CalendarScope, range: CalendarRangeQuery, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.calendar.all, scope, range] as const,
    queryFn: ({ signal }) => calendarRequest(scope, range, signal),
    enabled,
    placeholderData: (previous) => previous,
    // Cards appear on their own when the recurrence runner creates them: refresh quietly while the
    // tab is visible (focus and reconnect refetch by default). The personal calendar has no
    // virtual occurrences and is kept fresh by realtime hints instead.
    refetchInterval: scope.kind === 'me' ? false : CALENDAR_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
}

/** What the calendar sends for a deadline (`PUT /cards/{id}/deadline` body). */
export interface DeadlineValue {
  date: string;
  time: string | null;
  start_date: string | null;
  start_time: string | null;
  /** Absent = keep the card's choice (a move or resize must not switch it). */
  overdue_enabled?: boolean;
}

function patchCalendars(client: QueryClient, change: (card: Card) => Card): void {
  client.setQueriesData<CalendarData>({ queryKey: queryKeys.calendar.all }, (data) =>
    data ? { ...data, cards: data.cards.map(change) } : data,
  );
}

/**
 * Move / resize a card's deadline from the calendar. Optimistic: every cached calendar shows
 * the new deadline at once; on failure the previous data comes back (and is refetched), on
 * success the server copy replaces the card. `at` and `state` are re-derived by the refetch.
 */
export function useChangeDeadline() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, deadline }: { cardId: string; deadline: DeadlineValue }) =>
      api.put<Card>(`/v1/cards/${cardId}/deadline`, deadline),
    onMutate: async ({ cardId, deadline }) => {
      await client.cancelQueries({ queryKey: queryKeys.calendar.all });
      const previous = client.getQueriesData<CalendarData>({ queryKey: queryKeys.calendar.all });
      patchCalendars(client, (card) =>
        card.id === cardId && card.deadline
          ? { ...card, deadline: { ...card.deadline, ...deadline } }
          : card,
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      for (const [key, data] of context?.previous ?? []) client.setQueryData(key, data);
    },
    onSettled: (card, _error, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
      void client.invalidateQueries({ queryKey: queryKeys.cards.detail(variables.cardId) });
      if (card) void invalidateBoard(client, card.placement.board_id);
    },
  });
}

/**
 * Create a task in a chosen column of any board: from the calendar (already with its deadline)
 * or from the workspace "Create task" dialog (deadline optional).
 */
export function useCreateCalendarCard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      columnId,
      title,
      deadline,
      labelIds,
      recurrence,
    }: {
      columnId: string;
      title: string;
      deadline?: DeadlineValue | null;
      /** Existing labels of the column's board; assigned in the same atomic request. */
      labelIds?: string[];
      /** Makes the new card the source of a recurring series (same transaction). */
      recurrence?: RecurrenceInput | null;
    }) =>
      api.post<Card>(`/v1/columns/${columnId}/cards`, {
        title,
        ...(deadline && { deadline }),
        ...(labelIds && labelIds.length > 0 && { label_ids: labelIds }),
        ...(recurrence && { recurrence }),
      }),
    onSuccess: (card) => {
      void client.invalidateQueries({ queryKey: queryKeys.calendar.all });
      void invalidateBoard(client, card.placement.board_id);
    },
  });
}
